/**
 * Reads the `brand_key` claim from the access session cookie.
 *
 * Near-mirror of the same-named helper in aegix-aim-react — duplicated
 * rather than extracted because the libs/themes/aegix package
 * intentionally avoids web-API dependencies (it's consumed by storybook
 * and ssr contexts where `document.cookie` is meaningless).
 *
 * Per ADR-118 Phase 2 the access session cookie is host-only and
 * per-app-named — the backend sets `jwt-auth-sms` for
 * client `sms` and keeps the legacy `jwt-auth` name only for the AIM
 * family. This helper mirrors the backend's `session_cookie_names()`
 * read order (`server/apps/core/client_identity.py`): per-app name
 * first, then the legacy name as fallback; the first PRESENT cookie
 * wins (an unparseable per-app token does not fall through to legacy,
 * matching the backend read sites).
 *
 * Returns `null` when no cookie / not parseable / no `brand_key` claim
 * (a token minted without the claim gracefully degrades to the hostname
 * fallback per ADR 115 §Backward compatibility).
 */
export function getBrandKeyFromAuthCookie(): string | null {
  if (typeof document === 'undefined') return null;

  // Both patterns anchor the FULL cookie name between an entry boundary
  // (start-of-string or `; `) and `=`, so the legacy `jwt-auth=` pattern
  // can never match inside another app's per-app entry such as
  // `jwt-auth-sms=` / `jwt-auth-<client>=` (the name is followed by `-`,
  // not `=`), and neither pattern prefix- or suffix-matches other names.
  const cookieMatch =
    document.cookie.match(/(?:^|;\s*)jwt-auth-sms=([^;]+)/) ??
    document.cookie.match(/(?:^|;\s*)jwt-auth=([^;]+)/);
  if (!cookieMatch) return null;

  const token = cookieMatch[1];
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    let payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payloadB64.length % 4 !== 0) payloadB64 += '=';
    const payloadJson = atob(payloadB64);
    const claims = JSON.parse(payloadJson) as Record<string, unknown>;
    const brandKey = claims['brand_key'];
    return typeof brandKey === 'string' ? brandKey : null;
  } catch {
    return null;
  }
}
